/**
 * Remove the legacy `size-full` class from images in migrated blog HTML.
 *
 * Articles brought over from the HubSpot blog carry WordPress markup:
 *
 *     <img src="…/Blog-Button_05.png" width="90" height="38"
 *          class="alignnone size-full wp-image-137114">
 *
 * Tailwind 3.4 owns that class name too — it compiles to `width:100%;height:100%`
 * — and it ships in our bundle because our own components use the utility. The
 * result is that a 90x38 apply button fills the table cell it sits in.
 *
 * It cannot be fixed in CSS. `width`/`height` attributes are presentational
 * hints, and those lose to ANY author declaration: `width:auto` hands the image
 * its intrinsic file size, not its attribute, and `width:revert` skips past the
 * hints to the UA origin and does the same. Measured on a migrated article
 * against the same article still served by HubSpot — a card whose file is
 * 880x569 and whose attributes say 300x189 renders 300x194 there and 419x271
 * with either declaration. The only thing that restores it is the class not
 * being on the element, which is what this does.
 *
 * Only `size-full` collides. `size-medium`, `size-sm`, `alignnone` and
 * `wp-image-*` have no rule in the bundle and are left alone — they are part of
 * the article's history and removing them buys nothing.
 */

/**
 * The `class` attribute of an `<img>`: the tag up to it, the quote, and the value.
 * Captured separately from the tag so the attribute can be dropped whole without
 * taking the rest of the tag with it.
 */
const IMG_CLASS = /(<img\b[^>]*?)\s*\bclass=(["'])(.*?)\2/gi;

export function stripLegacySizeClasses(htmlString: string): string {
  if (!htmlString.includes('size-full')) return htmlString;

  return htmlString.replace(IMG_CLASS, (whole, lead: string, quote: string, value: string) => {
    const kept = value
      .split(/\s+/)
      .filter((cls) => cls && cls !== 'size-full')
      .join(' ');
    // Drop the attribute entirely when nothing else was on it, rather than
    // leaving `class=""` behind.
    return kept ? `${lead} class=${quote}${kept}${quote}` : lead;
  });
}
